/**
 * O que faz: Lista leads que receberam follow-up e abre o chat sem sair da tela.
 * Onde usa: Métricas da página Follow-up.
 * Conexões: Dialog shadcn, ChatWindow, Tabs e filtro de resposta em analytics.
 * Tabelas: Nenhuma direta; recebe dados já filtrados por follow_up_execucoes.
 * Data: 27/08/2026 (cores do Gens Design System em 07/10/2026)
 */

import { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { Calendar, ChevronLeft, ExternalLink, MessageCircle, Phone, Send, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ChatWindow } from '@/components/chat/ChatWindow';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ThinkingOrbLoader } from '@/components/ui/ThinkingOrbLoader';
import {
  calcularMinutosAteResposta,
  FILTRO_RESPOSTA_FOLLOWUP,
  filtrarLeadsPorResposta,
  formatarDuracaoFollowup,
  type FiltroRespostaFollowup,
} from '@/features/followup/analytics';
import type { FollowupDispatchedLead } from '@/features/followup/dispatchedLeads';
import { cn } from '@/lib/utils';

interface FollowupDispatchedLeadsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leads: FollowupDispatchedLead[];
  isLoading: boolean;
  isAdminMode: boolean;
  targetClinicaId?: string | null;
  campaignName?: string | null;
  filtroResposta?: FiltroRespostaFollowup;
  onFiltroRespostaChange?: (filtro: FiltroRespostaFollowup) => void;
}

const formatPhone = (phone: string | null) => {
  if (!phone) return 'Telefone não informado';
  const digits = phone.replace(/\D/g, '');
  return digits.length === 11
    ? `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`
    : phone;
};

const formatDateTime = (value: string) => format(new Date(value), 'dd/MM/yyyy HH:mm');

export const FollowupDispatchedLeadsDialog = ({
  open,
  onOpenChange,
  leads,
  isLoading,
  isAdminMode,
  targetClinicaId,
  campaignName = null,
  filtroResposta = FILTRO_RESPOSTA_FOLLOWUP.todos,
  onFiltroRespostaChange,
}: FollowupDispatchedLeadsDialogProps) => {
  const navigate = useNavigate();
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) setSelectedLeadId(null);
  }, [open]);

  const contagem = useMemo(() => ({
    todos: leads.length,
    respondidos: leads.filter((lead) => Boolean(lead.respondedAt)).length,
    sem_resposta: leads.filter((lead) => !lead.respondedAt).length,
  }), [leads]);

  const leadsFiltrados = useMemo(
    () => filtrarLeadsPorResposta(leads, filtroResposta),
    [leads, filtroResposta],
  );

  useEffect(() => {
    if (selectedLeadId && !leadsFiltrados.some((item) => item.leadId === selectedLeadId)) {
      setSelectedLeadId(null);
    }
  }, [leadsFiltrados, selectedLeadId]);

  const selectedLead = leadsFiltrados.find((item) => item.leadId === selectedLeadId) ?? null;
  const chatClinicaId = isAdminMode ? targetClinicaId : selectedLead?.clinicaId;
  const title = campaignName ? `Leads disparados: ${campaignName}` : 'Leads disparados pelo follow-up';

  const handleOpenMainChat = () => {
    if (!selectedLead) return;

    const params = new URLSearchParams({ leadId: selectedLead.leadId });
    if (isAdminMode && chatClinicaId) {
      params.set('adminClinicId', chatClinicaId);
    }

    onOpenChange(false);
    navigate(`/chat?${params.toString()}`);
  };

  const emptyMessage = () => {
    if (filtroResposta === FILTRO_RESPOSTA_FOLLOWUP.respondidos) {
      return {
        title: 'Nenhum lead respondeu neste recorte.',
        subtitle: 'Tente ampliar o período ou confira a aba Sem resposta.',
      };
    }
    if (filtroResposta === FILTRO_RESPOSTA_FOLLOWUP.sem_resposta) {
      return {
        title: 'Todos os leads deste recorte responderam.',
        subtitle: 'Ótimo sinal de reativação para o período selecionado.',
      };
    }
    return {
      title: 'Nenhum follow-up foi enviado neste período.',
      subtitle: 'Ajuste o período ou aguarde o próximo disparo da campanha.',
    };
  };

  const empty = emptyMessage();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gens-escopo flex h-[100dvh] w-[100vw] max-w-none flex-col gap-0 overflow-hidden rounded-none p-0 sm:h-[88vh] sm:w-[94vw] sm:max-w-6xl sm:rounded-3xl">
        <DialogHeader className="border-b bg-card px-4 py-4 sm:px-6">
          <DialogTitle className="flex items-center gap-2 text-left text-base text-foreground">
            <Send className="h-4 w-4 text-accent-foreground" />
            {title}
          </DialogTitle>
          <p className="text-left text-xs text-muted-foreground">Selecione um lead para conferir a conversa e o contexto do último envio.</p>
        </DialogHeader>

        <div className="relative flex min-h-0 flex-1 overflow-hidden">
          <section className={cn('flex min-w-0 flex-1 flex-col border-r bg-card', selectedLead && 'hidden sm:flex')}>
            <div className="border-b px-4 py-3 sm:px-5">
              <Tabs
                value={filtroResposta}
                onValueChange={(value) => onFiltroRespostaChange?.(value as FiltroRespostaFollowup)}
              >
                <TabsList className="h-auto w-full flex-wrap justify-start gap-1 bg-secondary p-1">
                  <TabsTrigger value={FILTRO_RESPOSTA_FOLLOWUP.todos} className="text-xs">
                    Todos ({contagem.todos})
                  </TabsTrigger>
                  <TabsTrigger value={FILTRO_RESPOSTA_FOLLOWUP.respondidos} className="text-xs">
                    Responderam ({contagem.respondidos})
                  </TabsTrigger>
                  <TabsTrigger value={FILTRO_RESPOSTA_FOLLOWUP.sem_resposta} className="text-xs">
                    Sem resposta ({contagem.sem_resposta})
                  </TabsTrigger>
                </TabsList>
                <TabsContent value={filtroResposta} className="mt-0" />
              </Tabs>
            </div>

            {isLoading ? (
              <div className="flex flex-1 items-center justify-center">
                <ThinkingOrbLoader state="working" size={64} label="Carregando leads..." />
              </div>
            ) : leadsFiltrados.length === 0 ? (
              <div className="flex flex-1 flex-col items-center justify-center px-6 text-center">
                <div className="mb-4 rounded-full bg-accent p-4">
                  <Send className="h-7 w-7 text-accent-foreground" />
                </div>
                <p className="font-medium text-foreground">{empty.title}</p>
                <p className="mt-1 text-sm text-muted-foreground">{empty.subtitle}</p>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto divide-y divide-border">
                {leadsFiltrados.map((lead) => {
                  const isSelected = lead.leadId === selectedLeadId;
                  const minutosResposta = calcularMinutosAteResposta(lead.lastSentAt, lead.respondedAt);
                  return (
                    <button
                      key={lead.leadId}
                      type="button"
                      aria-label={`Ver chat de ${lead.nome || 'lead sem nome'}`}
                      onClick={() => setSelectedLeadId(lead.leadId)}
                      className={cn(
                        'w-full px-4 py-4 text-left transition-colors sm:px-5',
                        isSelected ? 'border-l-4 border-ring bg-accent' : 'border-l-4 border-transparent hover:bg-muted',
                      )}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate font-medium text-foreground">{lead.nome || 'Nome não informado'}</p>
                          <p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                            <Phone className="h-3.5 w-3.5" />
                            {formatPhone(lead.telefone)}
                          </p>
                        </div>
                        <MessageCircle className="mt-0.5 h-4 w-4 shrink-0 text-accent-foreground" />
                      </div>
                      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                        <span className="font-medium text-accent-foreground">{lead.campaignName}</span>
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5" />
                          {formatDateTime(lead.lastSentAt)}
                        </span>
                        <span>
                          {lead.receivedFollowups} follow-up{lead.receivedFollowups === 1 ? ' recebido' : 's recebidos'}
                        </span>
                        {lead.respondedAt ? (
                          <span className="text-sky-700">
                            Respondeu em {formatarDuracaoFollowup(minutosResposta)}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">Sem resposta ainda</span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </section>

          <section className={cn('absolute inset-0 flex min-w-0 flex-1 flex-col bg-card sm:relative', selectedLead ? 'translate-x-0' : 'translate-x-full sm:translate-x-0')}>
            {selectedLead ? (
              <>
                <div className="flex items-center gap-3 border-b bg-accent/60 px-4 py-3">
                  <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => setSelectedLeadId(null)}>
                    <ChevronLeft className="h-4 w-4 sm:hidden" />
                    <X className="hidden h-4 w-4 sm:block" />
                    <span className="ml-1 text-xs">Voltar</span>
                  </Button>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-foreground">{selectedLead.nome || 'Nome não informado'}</p>
                    <p className="text-xs text-muted-foreground">Último follow-up: {formatDateTime(selectedLead.lastSentAt)}</p>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-8 shrink-0 gap-1.5 text-xs"
                    onClick={handleOpenMainChat}
                    aria-label="Abrir no chat"
                    title="Abrir esta conversa na aba de chat"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">Abrir no chat</span>
                  </Button>
                </div>
                <div className="min-h-0 flex-1">
                  <ChatWindow leadId={selectedLead.leadId} clinicaId={chatClinicaId ?? null} isAdminMode={isAdminMode} />
                </div>
              </>
            ) : (
              <div className="hidden flex-1 flex-col items-center justify-center bg-muted/50 p-8 text-center sm:flex">
                <MessageCircle className="mb-3 h-8 w-8 text-muted-foreground/60" />
                <p className="font-medium text-muted-foreground">Nenhum chat selecionado</p>
                <p className="mt-1 text-sm text-muted-foreground">Escolha um lead da lista para ler a conversa.</p>
              </div>
            )}
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
};
